/*
 * HAKI native size guide — storefront renderer.
 *
 * Replaces the Kiwi Sizing app widget. The chart is authored in the PLM app and
 * written to the product metafield `custom.size_chart_data` (type: json,
 * storefront PUBLIC_READ). `snippets/haki-size-guide.liquid` dumps that payload
 * verbatim into a <script type="application/json"> tag; this file parses it and
 * builds the DOM.
 *
 * Payload contract (v1) — do NOT change without changing the PLM writer:
 *   { v, id, title,
 *     sections: [
 *       { id, type:'table',   rows:[[{raw,num,unit},…],…], columnUnits:[null,'in'],
 *                             headerText?, footerText?, hidden?, stickyHeader? },
 *       { id, type:'image',   url, alt, widthPercent },
 *       { id, type:'text',    text, align },
 *       { id, type:'divider', label? }
 *     ],
 *     unitConfig: { defaultSystem:'imperial'|'metric', showToggle, maxDecimals },
 *     recommender: null | { enabled, inputMode, sectionId, category, columns:[…],
 *                           fitPreferenceEnabled, defaultFitPreference } }
 *
 * `title` is the GARMENT-TYPE label and nothing else — "Tee Sizing Chart",
 * "Hoodie Sizing Chart", "Shorts Sizing Chart". It may be absent or empty. It
 * must never carry a product name, because ONE CHART SERVES MANY PRODUCTS: the
 * tee chart is on every tee, so "Tee Sizing Chart - Fire Fist Jorts" is wrong on
 * every product except the one someone happened to name it after. The heading
 * the shopper reads is composed server-side in snippets/haki-size-guide.liquid
 * as `title – <the product's own title>`, product title from Liquid, which is
 * the only place that fact is known per-page. This file does not recompose it;
 * it reads the rendered <h2> back so the heading and the tables' accessible
 * names cannot drift apart.
 *
 * There is NO `name` field any more, and nothing here may fall back to one.
 * `name` was PLM's internal ADMIN label for the chart ("Tee Sizing Chart -
 * Mandy v1", "Akimbo" — a shape codename, not a product) and it was going
 * straight into the modal title in front of shoppers. Old metafields still hold
 * it until PLM's hourly reconciler rewrites them, so a fallback would keep
 * leaking exactly what removing the field is fixing. A chart that reads as just
 * the product title, or as "Size Guide", for those few minutes is correct.
 *
 * `recommender` drives the optional second tab, "What's my size?". Its shape is
 * owned by PLM's lib/sizing/recommender-core.ts and is validated here by that
 * module's own sanitiser (assets/haki-size-recommender.js) rather than by any
 * check written in this file. Absent or disabled => no tabs at all, and the
 * modal is byte-for-byte what it was.
 *
 * Cell rules (these ARE the product — keep them identical to the PLM preview):
 *   1. columnUnits[i] === null  OR  cell.num === null  ->  print cell.raw VERBATIM.
 *      This is the deliberate fix for Kiwi's bug where non-numeric cells ("One
 *      size", "N/A", size labels) went blank the moment you hit the unit toggle.
 *   2. otherwise the cell's unit is `cell.unit ?? columnUnits[i]`; in->cm x2.54,
 *      cm->in /2.54, no-op if already in the target system.
 *   3. round to unitConfig.maxDecimals and DROP trailing zeros (10.0 -> "10").
 *   4. a range ("30-32", ASCII hyphen, checked on cell.raw with units stripped)
 *      converts END BY END -> "76.2-81.3". Never as a midpoint.
 *
 * Everything is inserted with textContent / createElement. No innerHTML anywhere:
 * `text` sections are merchant-authored plain text, not HTML.
 *
 * No dependencies, no build step, ES5-safe.
 */
(function () {
	'use strict';

	var CM_PER_IN = 2.54;
	var RANGE_RE = /^(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)$/;

	/* ---------------------------------------------------------------- units */

	function normaliseUnit(unit) {
		if (unit === null || unit === undefined) return null;
		var u = String(unit).toLowerCase().trim().replace(/\.$/, '');
		if (u === 'in' || u === 'inch' || u === 'inches' || u === '"') return 'in';
		if (
			u === 'cm' ||
			u === 'cms' ||
			u === 'centimeter' ||
			u === 'centimeters' ||
			u === 'centimetre' ||
			u === 'centimetres'
		) {
			return 'cm';
		}
		// Unknown unit string: treat as "not convertible" so the raw text survives.
		return null;
	}

	function systemToUnit(system) {
		return String(system).toLowerCase() === 'metric' ? 'cm' : 'in';
	}

	function convert(value, from, to) {
		if (from === to) return value;
		if (from === 'in' && to === 'cm') return value * CM_PER_IN;
		if (from === 'cm' && to === 'in') return value / CM_PER_IN;
		return value;
	}

	// Round half-up to `dp` decimals, then let String() drop the trailing zeros
	// (10 -> "10", 10.30 -> "10.3"). Number.EPSILON nudges values that binary
	// float parks a hair under the .5 boundary (e.g. 1.005).
	function formatNumber(value, dp) {
		var factor = Math.pow(10, dp);
		var rounded = Math.round((value + Number.EPSILON) * factor) / factor;
		if (rounded === 0) rounded = 0; // kill -0
		return String(rounded);
	}

	// Strip unit words/marks so "30-32 in" and '30-32"' still read as ranges.
	//
	// Dashes are normalised to ASCII first. Merchants paste from spreadsheets,
	// PDFs and editors that auto-substitute en/em dashes, and PLM's parser
	// (lib/sizing/core.ts normalizeDashes) accepts all of them — so if this did
	// not, "30–32" would render as its midpoint here and as "76.2-81.3" in the
	// PLM preview. Same input, two different tables. Kiwi's answer to this was
	// to reject the dash and blame the merchant's text editor.
	function stripUnits(raw) {
		return String(raw)
			.replace(/[‐-―−]/g, '-')
			.replace(/[”″"'’]/g, '')
			.replace(
				/(\d)\s*(?:inches|inch|in|centimetres|centimeters|centimetre|centimeter|cms|cm)\b\.?/gi,
				'$1'
			)
			.replace(
				/\b(?:inches|inch|in|centimetres|centimeters|centimetre|centimeter|cms|cm)\b\.?/gi,
				''
			)
			.replace(/\s+/g, '')
			.trim();
	}

	/**
	 * The whole ballgame. Returns the string to print in one cell.
	 * @param {object} cell        {raw, num, unit}
	 * @param {?string} columnUnit columnUnits[i]
	 * @param {string} targetUnit  'in' | 'cm'
	 * @param {number} maxDecimals
	 */
	function cellText(cell, columnUnit, targetUnit, maxDecimals) {
		if (!cell || typeof cell !== 'object') return '';
		var raw = cell.raw === null || cell.raw === undefined ? '' : String(cell.raw);

		var colUnit = normaliseUnit(columnUnit);
		var num = cell.num;
		var numeric = typeof num === 'number' && isFinite(num);

		// RULE 1 — no column unit, or nothing numeric to convert: verbatim.
		if (colUnit === null || !numeric) return raw;

		// RULE 2 — cell.unit wins over the column unit when it is set (and known).
		var fromUnit = normaliseUnit(cell.unit) || colUnit;

		// RULE 4 — ranges convert end by end.
		var range = RANGE_RE.exec(stripUnits(raw));
		if (range) {
			return (
				formatNumber(convert(parseFloat(range[1]), fromUnit, targetUnit), maxDecimals) +
				'-' +
				formatNumber(convert(parseFloat(range[2]), fromUnit, targetUnit), maxDecimals)
			);
		}

		// RULE 3 — single value.
		return formatNumber(convert(num, fromUnit, targetUnit), maxDecimals);
	}

	/* ----------------------------------------------------------------- DOM  */

	function el(tag, className) {
		var node = document.createElement(tag);
		if (className) node.className = className;
		return node;
	}

	function isRenderable(section) {
		if (!section || typeof section !== 'object') return false;
		switch (section.type) {
			case 'table':
				// hidden tables only feed the (later-phase) recommender — never rendered.
				return section.hidden !== true && Array.isArray(section.rows) && section.rows.length > 0;
			case 'image':
				return typeof section.url === 'string' && section.url.length > 0;
			case 'text':
				return typeof section.text === 'string' && section.text.trim().length > 0;
			case 'divider':
				return true;
			default:
				return false;
		}
	}

	/**
	 * Build one table. Row 0 is the header row; the first column of each body row
	 * is a row header, which is what makes a size table readable in a screen
	 * reader ("Waist, size M, 28.3").
	 * Returns { node, cells } where `cells` is the list of live cells the unit
	 * toggle re-renders in place.
	 */
	function buildTable(section, label, state) {
		var cells = [];
		var wrap = el('div', 'haki-sg__section haki-sg__section--table');
		var columnUnits = Array.isArray(section.columnUnits) ? section.columnUnits : [];

		if (typeof section.headerText === 'string' && section.headerText.trim()) {
			var head = el('p', 'haki-sg__note');
			head.textContent = section.headerText;
			wrap.appendChild(head);
		}

		// Wide charts scroll INSIDE this box — the modal itself never scrolls
		// sideways. tabindex=0 so a keyboard-only user can actually scroll it
		// (axe: scrollable-region-focusable; see ADA-AUDIT-2026-07-08.md).
		var scroller = el('div', 'haki-sg__table-wrap');
		scroller.setAttribute('tabindex', '0');
		scroller.setAttribute('role', 'group');
		scroller.setAttribute('aria-label', label);

		var table = el('table', 'haki-sg__table');
		if (section.stickyHeader !== false) table.setAttribute('data-sticky-header', 'true');

		var rows = section.rows;

		function makeCell(tag, cell, colIndex, scope) {
			var node = el(tag, null);
			if (scope) node.setAttribute('scope', scope);
			var colUnit = colIndex < columnUnits.length ? columnUnits[colIndex] : null;
			node.textContent = cellText(cell, colUnit, state.unit, state.maxDecimals);
			// Only cells that can actually change need re-rendering on toggle.
			if (normaliseUnit(colUnit) !== null) {
				cells.push({node: node, cell: cell, columnUnit: colUnit});
			}
			return node;
		}

		var thead = el('thead', null);
		var headRow = el('tr', null);
		(rows[0] || []).forEach(function (cell, i) {
			headRow.appendChild(makeCell('th', cell, i, 'col'));
		});
		thead.appendChild(headRow);
		table.appendChild(thead);

		if (rows.length > 1) {
			var tbody = el('tbody', null);
			for (var r = 1; r < rows.length; r++) {
				var row = Array.isArray(rows[r]) ? rows[r] : [];
				var tr = el('tr', null);
				for (var c = 0; c < row.length; c++) {
					tr.appendChild(makeCell(c === 0 ? 'th' : 'td', row[c], c, c === 0 ? 'row' : null));
				}
				tbody.appendChild(tr);
			}
			table.appendChild(tbody);
		}

		scroller.appendChild(table);
		wrap.appendChild(scroller);

		if (typeof section.footerText === 'string' && section.footerText.trim()) {
			var foot = el('p', 'haki-sg__note');
			foot.textContent = section.footerText;
			wrap.appendChild(foot);
		}

		return {node: wrap, cells: cells};
	}

	/**
	 * Ask Shopify's image CDN for a resized copy, or return null to leave the URL
	 * alone.
	 *
	 * The diagrams are 1024px PNGs weighing 200-780KB and render at ~400 CSS px,
	 * so the full-size file is most of a megabyte of nothing. Shopify's CDN does
	 * the resize on a `width` query param — but the URL here is MERCHANT-supplied
	 * and only guaranteed to be https, so the rewrite is gated on the host really
	 * being a Shopify CDN. Bolting an unknown param onto someone else's URL can
	 * 404 it or invalidate a signature, and a size guide whose diagram is a
	 * broken-image icon is worse than a slow one. URLSearchParams preserves any
	 * existing query string (these all carry ?v=…) and replaces `width` rather
	 * than appending a second copy of it.
	 */
	function cdnResize(url, width) {
		try {
			var u = new URL(url, document.baseURI);
			var host = u.hostname.toLowerCase();
			var isShopify =
				/(?:^|\.)cdn\.shopify\.com$/.test(host) ||
				/\.shopifycdn\.net$/.test(host) ||
				// The same CDN proxied through the shop's own domain.
				u.pathname.indexOf('/cdn/shop/') === 0;
			if (!isShopify) return null;
			u.searchParams.set('width', String(width));
			return u.href;
		} catch (e) {
			// Not a URL we can parse — hand it back untouched.
			return null;
		}
	}

	// The diagram column measures ~450-545 CSS px across desktop sizes and up to
	// the modal's full width on a phone; the top entries are the retina copies.
	// 1200 is past the sources' own 1024px — Shopify's CDN never upscales, so it
	// simply returns the original, which is what a 2x screen wants anyway.
	var IMAGE_WIDTHS = [400, 600, 800, 1200];

	function buildImage(section) {
		var wrap = el('div', 'haki-sg__section haki-sg__section--image');
		var img = el('img', 'haki-sg__image');

		var resized = cdnResize(section.url, 800);
		img.setAttribute('src', resized || section.url);
		if (resized) {
			img.setAttribute(
				'srcset',
				IMAGE_WIDTHS.map(function (w) {
					return cdnResize(section.url, w) + ' ' + w + 'w';
				}).join(', ')
			);
			// Mirrors the CSS: the aside column above 990px (which stops growing
			// once the modal hits its 1520px ceiling), near-full width below.
			img.setAttribute(
				'sizes',
				'(min-width: 1750px) 545px, (min-width: 990px) 31vw, (min-width: 750px) 60vw, 88vw'
			);
		}

		// An explicit ratio so the modal does not reflow when the diagram decodes
		// (theme-check has ImgWidthAndHeight for exactly this). The payload carries
		// no dimensions today, so absent real ones we hint the square that every
		// HAKI diagram is. That hint is safe to be wrong about: width/height
		// attributes map to `aspect-ratio: auto W / H`, and the `auto` means the
		// browser swaps in the file's TRUE ratio the moment it decodes. A bad guess
		// therefore costs one reflow — the same one we have today with no hint at
		// all — and can never distort the image, because the CSS leaves both
		// dimensions auto.
		var w = Number(section.width);
		var h = Number(section.height);
		if (!(isFinite(w) && w > 0 && isFinite(h) && h > 0)) {
			w = 1024;
			h = 1024;
		}
		img.setAttribute('width', String(Math.round(w)));
		img.setAttribute('height', String(Math.round(h)));

		// Decorative diagrams still need an explicit alt="" rather than none.
		img.setAttribute('alt', typeof section.alt === 'string' ? section.alt : '');
		img.setAttribute('loading', 'lazy');
		img.setAttribute('decoding', 'async');
		// max-width, not width: widthPercent is a ceiling ("don't let this fill the
		// column"), and as a fixed width it would fight the max-height cap and hand
		// object-fit a letterboxed box to draw into. Identical in practice — the
		// sources are 1024px and no column here is wider than that.
		var pct = Number(section.widthPercent);
		if (isFinite(pct) && pct > 0 && pct <= 100) img.style.maxWidth = pct + '%';

		wrap.appendChild(img);
		// `image` is handed back so the render loop can pair each instruction box
		// with the diagram it belongs to — see fitInstructionBoxes().
		return {node: wrap, cells: [], image: img};
	}

	/**
	 * A text section.
	 *
	 * `boxed` turns it into the framed, height-capped measuring-instructions box
	 * (see the .haki-sg__howto block in haki-size-guide.css). It is true only on
	 * charts that split into two columns — i.e. charts that actually have a
	 * diagram for the box to sit beside. A text-only chart, or a chart with no
	 * diagram at all, renders exactly the bare paragraphs it always did: there is
	 * nothing there to align a height to, and framing a lone block of prose in a
	 * 720px dialog would be decoration rather than structure.
	 *
	 * `groupLabel` is the heading of the garment this text belongs to, carried in
	 * from the divider the composer emits above each half of a bundle guide. It
	 * only ever reaches the accessible name, so that a two-garment guide does not
	 * announce two regions both called "Measuring instructions".
	 */
	function buildText(section, boxed, groupLabel) {
		var wrap = el('div', 'haki-sg__section haki-sg__section--text');
		if (boxed) wrap.classList.add('haki-sg__howto');
		var align = section.align;
		if (align === 'center' || align === 'right') wrap.style.textAlign = align;

		// Boxed, the paragraphs live inside a scroller of their own — that inner
		// element is what carries the height cap, so it is the element that can
		// overflow, and therefore the element a keyboard-only user has to be able
		// to reach and arrow through (WCAG 2.1.1; axe calls it
		// scrollable-region-focusable). tabindex/role/aria-label are exactly what
		// buildTable puts on its own scroll box, and unconditionally for the same
		// reason: whether this one scrolls depends on the viewport and on how tall
		// the paired diagram came out, and a tab stop that appears and disappears
		// as the window is resized is worse than one that is always there.
		var host = wrap;
		if (boxed) {
			host = el('div', 'haki-sg__howto-scroll');
			host.setAttribute('tabindex', '0');
			host.setAttribute('role', 'group');
			host.setAttribute(
				'aria-label',
				groupLabel ? 'Measuring instructions – ' + groupLabel : 'Measuring instructions'
			);
			wrap.appendChild(host);
		}

		// Blank lines split paragraphs; single newlines are preserved by
		// `white-space: pre-line` in the stylesheet. Plain text, never HTML.
		String(section.text)
			.split(/\n[ \t]*\n/)
			.forEach(function (para) {
				if (!para.trim()) return;
				var p = el('p', 'haki-sg__para');
				p.textContent = para.replace(/^\n+|\n+$/g, '');
				host.appendChild(p);
			});
		return {node: wrap, cells: [], scroller: boxed ? host : null};
	}

	function buildDivider(section) {
		var wrap = el('div', 'haki-sg__section haki-sg__section--divider');
		var label = typeof section.label === 'string' ? section.label.trim() : '';
		if (label) {
			wrap.classList.add('haki-sg__divider--labelled');
			var span = el('span', 'haki-sg__divider-label');
			span.textContent = label;
			wrap.appendChild(span);
		}
		wrap.setAttribute('role', 'separator');
		return {node: wrap, cells: []};
	}

	/* --------------------------------------------------------- recommender */

	/*
	 * "What's my size?" — the optional second tab.
	 *
	 * THE SCORING MATHS IS NOT IN THIS FILE. `window.HakiSizing` is a generated
	 * bundle of PLM's lib/sizing/recommender-core.ts (assets/haki-size-recommender.js,
	 * produced by plm's scripts/build-sizing-asset.ts). One implementation, two
	 * places: were the storefront to score sizes with its own copy of the
	 * arithmetic, the merchant's preview inside PLM and the shopper's answer here
	 * would eventually disagree — silently, with a return as the only symptom.
	 *
	 * So everything below is UI. Every parse ("31 1/2", "31,5", '31½"', "80cm"),
	 * every unit conversion, every validation bound and every verdict word comes
	 * out of the engine's exports. Nothing here re-derives one.
	 *
	 * The tab renders only when the payload's `recommender` survives the engine's
	 * own sanitiser (which returns null for `enabled !== true`, for a config
	 * pointing at a deleted table, and for column indexes that no longer exist).
	 * With no recommender there is no tab strip at all and the modal is exactly
	 * what it was — which is the state of all eleven charts live today.
	 */

	// Namespaced so it cannot collide with anything else on the store, and
	// versioned so a future shape change does not have to read old junk.
	var LS_KEY = 'haki:size-recommender:v1';

	// Shopper-facing names for the engine's canonical measurements. The KEY is
	// what everything is stored and looked up by — never the merchant's column
	// header. A header is free text: "WAIST" today, "Waist " with a stray space
	// tomorrow, and a shopper's saved measurements would silently vanish. (Kiwi
	// keys its saved profile by field name and loses them on exactly that typo.)
	var CANON_LABELS = {
		chest: 'Chest / bust',
		waist: 'Waist',
		hip: 'Hip',
		inseam: 'Inseam',
		outseam: 'Outseam',
		front_rise: 'Front rise',
		leg_opening: 'Leg opening',
		sleeve: 'Sleeve length',
		shoulder: 'Shoulder width',
		length: 'Length',
		thigh: 'Thigh',
		neck: 'Neck'
	};

	function engine() {
		var e = window.HakiSizing;
		return e && typeof e.recommendSize === 'function' ? e : null;
	}

	/* --- saved answers ------------------------------------------------- */

	function loadSaved() {
		try {
			var raw = window.localStorage.getItem(LS_KEY);
			if (!raw) return {};
			var v = JSON.parse(raw);
			return v && typeof v === 'object' ? v : {};
		} catch (e) {
			// Safari private mode throws on access. A remembered measurement is a
			// convenience; never let it be the reason the widget fails.
			return {};
		}
	}

	function saveAnswers(patch) {
		try {
			var cur = loadSaved();
			for (var k in patch) {
				if (Object.prototype.hasOwnProperty.call(patch, k)) cur[k] = patch[k];
			}
			window.localStorage.setItem(LS_KEY, JSON.stringify(cur));
		} catch (e) {
			/* storage full, disabled, or private mode — nothing to do about it */
		}
	}

	/* --- small parsing helpers (NOT measurement parsing — that is the engine) */

	function plainNumber(text) {
		var s = String(text === null || text === undefined ? '' : text).trim();
		if (!s) return null;
		// "70,5" is a decimal comma everywhere this widget ships.
		var n = Number(s.replace(',', '.'));
		return isFinite(n) ? n : null;
	}

	// A unit written into the box beats the toggle beside it, mirroring the
	// engine's parseBodyInput. Someone who typed "kg" meant kg.
	function parseWeight(text, unit) {
		var s = String(text === null || text === undefined ? '' : text).toLowerCase();
		var own = null;
		if (/\bkgs?\b|kilo/.test(s)) own = 'kg';
		else if (/\blbs?\b|pound/.test(s)) own = 'lb';
		var n = plainNumber(s.replace(/[^0-9.,\-]/g, ''));
		if (n === null) return null;
		return {unit: own || unit, value: n};
	}

	function round1(n) {
		return Math.round(n * 10) / 10;
	}

	/* --- DOM helpers ---------------------------------------------------- */

	function textNode(tag, className, text) {
		var n = el(tag, className);
		n.textContent = text;
		return n;
	}

	/**
	 * A real tab list: roving tabindex, arrow/Home/End keys, aria-selected and
	 * aria-controls on every tab, one tabpanel per tab. Used for the top-level
	 * strip and for the "both" input mode's sub-tabs.
	 */
	function wireTabs(list, tabs, panels, onSelect) {
		list.setAttribute('role', 'tablist');

		function select(index, moveFocus) {
			for (var j = 0; j < tabs.length; j++) {
				var on = j === index;
				tabs[j].setAttribute('aria-selected', on ? 'true' : 'false');
				tabs[j].setAttribute('tabindex', on ? '0' : '-1');
				tabs[j].classList.toggle('is-active', on);
				panels[j].hidden = !on;
			}
			if (moveFocus) tabs[index].focus();
			if (onSelect) onSelect(index);
		}

		function bind(i) {
			tabs[i].setAttribute('role', 'tab');
			tabs[i].addEventListener('click', function () {
				select(i, false);
			});
			tabs[i].addEventListener('keydown', function (event) {
				var key = event.key;
				var next = -1;
				if (key === 'ArrowRight' || key === 'ArrowDown') next = (i + 1) % tabs.length;
				else if (key === 'ArrowLeft' || key === 'ArrowUp') next = (i - 1 + tabs.length) % tabs.length;
				else if (key === 'Home') next = 0;
				else if (key === 'End') next = tabs.length - 1;
				else return;
				event.preventDefault();
				select(next, true);
			});
			panels[i].setAttribute('role', 'tabpanel');
			panels[i].setAttribute('aria-labelledby', tabs[i].id);
			// Focusable so a keyboard user lands in (and can scroll) the panel.
			panels[i].setAttribute('tabindex', '0');
			tabs[i].setAttribute('aria-controls', panels[i].id);
		}

		for (var i = 0; i < tabs.length; i++) bind(i);
		select(0, false);
		return {select: select};
	}

	function makeTabButton(id, controls, text) {
		var b = el('button', 'haki-sg__tab');
		b.type = 'button';
		b.id = id;
		b.setAttribute('aria-controls', controls);
		b.textContent = text;
		return b;
	}

	/**
	 * One labelled text input with an optional unit toggle beside it.
	 *
	 * type="text" + inputmode, never type="number": the engine accepts "31 1/2",
	 * "31,5" and '31½"', all of which a number input silently refuses to hold.
	 *
	 * Returns a handle with the pieces the form needs; `units` is a list of
	 * {value,label} and may be empty.
	 */
	function buildField(opts) {
		var wrap = el('div', 'haki-sg__field');
		var input = el('input', 'haki-sg__input');
		input.type = 'text';
		input.id = opts.id;
		input.setAttribute('inputmode', 'decimal');
		input.setAttribute('autocomplete', 'off');
		if (opts.placeholder) input.setAttribute('placeholder', opts.placeholder);

		var label = el('label', 'haki-sg__label');
		label.setAttribute('for', input.id);
		label.textContent = opts.label;
		if (opts.optional) {
			label.appendChild(textNode('span', 'haki-sg__optional', ' (optional)'));
		}
		wrap.appendChild(label);

		var describedBy = [];
		var hint = null;
		if (opts.hint) {
			hint = textNode('p', 'haki-sg__hint', opts.hint);
			hint.id = opts.id + '-hint';
			describedBy.push(hint.id);
			wrap.appendChild(hint);
		}

		var error = el('p', 'haki-sg__field-error');
		error.id = opts.id + '-error';
		error.hidden = true;
		describedBy.push(error.id);
		input.setAttribute('aria-describedby', describedBy.join(' '));

		var row = el('div', 'haki-sg__field-row');
		var inputs = el('div', 'haki-sg__field-inputs');
		inputs.appendChild(input);

		// Second box, used only by the height field in feet+inches mode.
		var input2 = null;
		var label2 = null;
		if (opts.secondLabel) {
			input2 = el('input', 'haki-sg__input haki-sg__input--short');
			input2.type = 'text';
			input2.id = opts.id + '-2';
			input2.setAttribute('inputmode', 'decimal');
			input2.setAttribute('autocomplete', 'off');
			input2.setAttribute('aria-describedby', describedBy.join(' '));
			label2 = el('label', 'haki-sg__label haki-sg__label--inline');
			label2.setAttribute('for', input2.id);
			label2.textContent = opts.secondLabel;
			inputs.appendChild(label2);
			inputs.appendChild(input2);
			label2.hidden = true;
			input2.hidden = true;
		}
		row.appendChild(inputs);

		var unitButtons = [];
		var unit = opts.units && opts.units.length ? opts.units[0].value : null;
		if (opts.units && opts.units.length > 1) {
			var group = el('div', 'haki-sg__unitpick');
			group.setAttribute('role', 'group');
			group.setAttribute('aria-label', opts.label + ' units');
			opts.units.forEach(function (u) {
				var b = el('button', 'haki-sg__unitbtn');
				b.type = 'button';
				b.textContent = u.label;
				b.setAttribute('data-unit', u.value);
				b.setAttribute('aria-pressed', 'false');
				group.appendChild(b);
				unitButtons.push(b);
			});
			row.appendChild(group);
		}

		wrap.appendChild(row);
		wrap.appendChild(error);

		var handle = {
			node: wrap,
			input: input,
			input2: input2,
			unit: unit,
			onUnitChange: null,
			getUnit: function () {
				return handle.unit;
			},
			setError: function (message) {
				if (message) {
					error.textContent = message;
					error.hidden = false;
					input.setAttribute('aria-invalid', 'true');
					if (input2) input2.setAttribute('aria-invalid', 'true');
				} else {
					error.textContent = '';
					error.hidden = true;
					input.removeAttribute('aria-invalid');
					if (input2) input2.removeAttribute('aria-invalid');
				}
			},
			paintUnits: function () {
				unitButtons.forEach(function (b) {
					var on = b.getAttribute('data-unit') === handle.unit;
					b.setAttribute('aria-pressed', on ? 'true' : 'false');
					b.classList.toggle('is-active', on);
				});
				if (input2 && label2) {
					var two = handle.unit === 'ft_in';
					input2.hidden = !two;
					label2.hidden = !two;
				}
			},
			setUnit: function (next, notify) {
				if (!next || next === handle.unit) return;
				var previous = handle.unit;
				handle.unit = next;
				handle.paintUnits();
				if (notify && handle.onUnitChange) handle.onUnitChange(previous, next);
			}
		};

		unitButtons.forEach(function (b) {
			b.addEventListener('click', function () {
				handle.setUnit(b.getAttribute('data-unit'), true);
			});
		});
		handle.paintUnits();
		return handle;
	}

	function buildRadioGroup(name, legend, options, initial) {
		var fs = el('fieldset', 'haki-sg__group');
		fs.appendChild(textNode('legend', 'haki-sg__legend', legend));
		var row = el('div', 'haki-sg__radios');
		var inputs = [];
		options.forEach(function (opt, i) {
			var id = name + '-' + i;
			var input = el('input', 'haki-sg__radio');
			input.type = 'radio';
			input.name = name;
			input.id = id;
			input.value = opt.value;
			if (opt.value === initial) input.checked = true;
			var label = el('label', 'haki-sg__radio-label');
			label.setAttribute('for', id);
			label.textContent = opt.label;
			row.appendChild(input);
			row.appendChild(label);
			inputs.push(input);
		});
		fs.appendChild(row);
		return {
			node: fs,
			value: function () {
				for (var i = 0; i < inputs.length; i++) {
					if (inputs[i].checked) return inputs[i].value;
				}
				return null;
			}
		};
	}

	/* --- the product page's size picker --------------------------------- */

	/**
	 * Ring the recommended size in the variant selector behind the modal.
	 *
	 * Deliberately does NOT select the variant: changing the shopper's selection
	 * (and with it the price, the URL and the add-to-cart target) from inside an
	 * advisory widget is a side effect nobody asked for. Match is tolerant
	 * (trim + case); no match does nothing at all, silently, and any DOM surprise
	 * is swallowed — a themed size picker is not this widget's contract and must
	 * never be able to break it.
	 */
	function highlightSizeOption(size) {
		try {
			var previous = document.querySelectorAll('.haki-sg-recommended');
			Array.prototype.forEach.call(previous, function (n) {
				n.classList.remove('haki-sg-recommended');
			});
			if (!size) return false;
			var want = String(size).trim().toLowerCase();
			if (!want) return false;

			var groups = document.querySelectorAll('.product-form__input--radios');
			var scoped = [];
			Array.prototype.forEach.call(groups, function (g) {
				var legend = g.querySelector('legend');
				if (legend && /size/i.test(legend.textContent || '')) scoped.push(g);
			});
			var search = scoped.length ? scoped : [document];

			for (var i = 0; i < search.length; i++) {
				var radios = search[i].querySelectorAll('input[type="radio"]');
				for (var r = 0; r < radios.length; r++) {
					var value = (radios[r].value || '').trim().toLowerCase();
					if (value !== want) continue;
					var target = radios[r].id
						? search[i].querySelector('label[for="' + radios[r].id + '"]')
						: null;
					if (!target) continue;
					target.classList.add('haki-sg-recommended');
					return true;
				}
			}
			return false;
		} catch (e) {
			return false;
		}
	}

	/* --- the panel ------------------------------------------------------ */

	/**
	 * Build the whole "What's my size?" panel.
	 * Returns {node, refresh} or null when there is nothing to build.
	 * `refresh` re-runs the recommendation, so the inches/cm toggle can update
	 * the fit readout along with the table.
	 */
	function buildRecommender(data, config, uid, state) {
		var HS = engine();
		if (!HS) return null;
		var section = HS.findSourceTable(data.sections, config.sectionId);
		if (!section) return null;

		var saved = loadSaved();
		var savedMeasurements =
			saved.measurements && typeof saved.measurements === 'object' ? saved.measurements : {};
		var savedUnits = saved.units && typeof saved.units === 'object' ? saved.units : {};

		var wrap = el('div', 'haki-sg__rec');
		var form = el('form', 'haki-sg__rec-form');
		form.setAttribute('novalidate', 'novalidate');
		wrap.appendChild(form);

		/* ---- which measurement columns can actually be answered ---------- */
		var canonicals = [];
		var canonHeaders = {};
		var filterColumns = [];
		(config.columns || []).forEach(function (col) {
			if (col.role === 'filter') {
				filterColumns.push(col);
				return;
			}
			if (col.role !== 'measurement' || !col.canonical) return;
			if (canonicals.indexOf(col.canonical) === -1) {
				canonicals.push(col.canonical);
				var header = section.rows[0] && section.rows[0][col.columnIndex];
				canonHeaders[col.canonical] = header && header.raw ? String(header.raw).trim() : '';
			}
		});

		// Nothing to ask and nothing to score: the engine would only ever answer
		// 'not_configured', so do not offer a tab that cannot work.
		if (!canonicals.length && !filterColumns.length) return null;

		var mode = 'simple';

		/* ---- simple: sex, height, weight, age ---------------------------- */
		var simplePanel = el('div', 'haki-sg__rec-fields');
		simplePanel.id = 'HakiSgRecSimple-' + uid;

		var sex = buildRadioGroup(
			'HakiSgSex-' + uid,
			'Body type the estimate uses',
			[{value: 'male', label: 'Men’s'}, {value: 'female', label: 'Women’s'}],
			saved.sex === 'female' ? 'female' : 'male'
		);
		simplePanel.appendChild(sex.node);

		var height = buildField({
			id: 'HakiSgHeight-' + uid,
			label: 'Height',
			units: [{value: 'cm', label: 'cm'}, {value: 'ft_in', label: 'ft + in'}],
			secondLabel: 'in',
			placeholder: '175'
		});
		simplePanel.appendChild(height.node);

		var weight = buildField({
			id: 'HakiSgWeight-' + uid,
			label: 'Weight',
			units: [{value: 'kg', label: 'kg'}, {value: 'lb', label: 'lb'}],
			placeholder: '70'
		});
		simplePanel.appendChild(weight.node);

		var age = buildField({
			id: 'HakiSgAge-' + uid,
			label: 'Age',
			optional: true,
			hint: 'Only nudges the waist estimate. Leave blank if you’d rather not say.',
			units: [],
			placeholder: '30'
		});
		simplePanel.appendChild(age.node);

		/* ---- measurements: one field per canonical column ---------------- */
		var measurePanel = el('div', 'haki-sg__rec-fields');
		measurePanel.id = 'HakiSgRecMeasure-' + uid;
		var measureFields = {};
		canonicals.forEach(function (canonical) {
			var header = canonHeaders[canonical];
			var friendly = CANON_LABELS[canonical] || canonical;
			var field = buildField({
				id: 'HakiSgM-' + canonical + '-' + uid,
				label: friendly,
				hint:
					header && header.toLowerCase() !== friendly.toLowerCase()
						? 'The chart’s “' + header + '” column.'
						: null,
				units: [{value: 'in', label: 'in'}, {value: 'cm', label: 'cm'}]
			});
			measureFields[canonical] = field;
			measurePanel.appendChild(field.node);
		});
		if (canonicals.length) {
			measurePanel.appendChild(
				textNode(
					'p',
					'haki-sg__hint',
					'Measure over your body, not over clothes. Fractions are fine - “31 1/2”, “31,5” and “80cm” all work.'
				)
			);
		}

		/* ---- mode switching ---------------------------------------------- */
		var offerBoth = config.inputMode === 'both' && canonicals.length > 0;
		if (offerBoth) {
			var subList = el('div', 'haki-sg__subtabs');
			var t1 = makeTabButton('HakiSgSubSimple-' + uid, simplePanel.id, 'Height & weight');
			var t2 = makeTabButton('HakiSgSubMeasure-' + uid, measurePanel.id, 'My measurements');
			subList.appendChild(t1);
			subList.appendChild(t2);
			form.appendChild(subList);
			form.appendChild(simplePanel);
			form.appendChild(measurePanel);
			var sub = wireTabs(subList, [t1, t2], [simplePanel, measurePanel], function (i) {
				mode = i === 0 ? 'simple' : 'measurements';
			});
			if (saved.mode === 'measurements') sub.select(1, false);
		} else if (!canonicals.length) {
			// A pure lookup table (a Breed or Fit column and nothing numeric).
			// Height and weight would be asked for and then scored against
			// nothing, so the filter dropdowns below are the entire form.
			mode = 'measurements';
		} else if (config.inputMode === 'measurements') {
			mode = 'measurements';
			form.appendChild(measurePanel);
		} else {
			mode = 'simple';
			form.appendChild(simplePanel);
		}

		/* ---- filter columns (a lookup table's dropdowns) ------------------ */
		var filterSelects = [];
		filterColumns.forEach(function (col) {
			var options = HS.filterOptions(section, col.columnIndex);
			if (!options.length) return;
			var headerCell = section.rows[0] && section.rows[0][col.columnIndex];
			var name = headerCell && headerCell.raw ? String(headerCell.raw).trim() : 'Option';
			var field = el('div', 'haki-sg__field');
			var id = 'HakiSgF-' + col.columnIndex + '-' + uid;
			var label = el('label', 'haki-sg__label');
			label.setAttribute('for', id);
			label.textContent = name;
			var select = el('select', 'haki-sg__select');
			select.id = id;
			var blank = el('option', null);
			blank.value = '';
			blank.textContent = 'Any';
			select.appendChild(blank);
			options.forEach(function (value) {
				var o = el('option', null);
				o.value = value;
				o.textContent = value;
				select.appendChild(o);
			});
			field.appendChild(label);
			field.appendChild(select);
			form.appendChild(field);
			filterSelects.push({columnIndex: col.columnIndex, select: select});
		});

		/* ---- fit preference ------------------------------------------------ */
		var fit = null;
		if (config.fitPreferenceEnabled) {
			fit = buildRadioGroup(
				'HakiSgFit-' + uid,
				'How do you like it to fit?',
				[
					{value: 'slim', label: 'Fitted'},
					{value: 'regular', label: 'Regular'},
					{value: 'loose', label: 'Loose'}
				],
				saved.fitPreference || config.defaultFitPreference || 'regular'
			);
			form.appendChild(fit.node);
		}

		var submit = el('button', 'haki-sg__rec-submit');
		submit.type = 'submit';
		submit.textContent = 'Find my size';
		form.appendChild(submit);

		/* ---- the answer ---------------------------------------------------- */
		// The live region holds ONLY the headline and any error, so a screen
		// reader announces "Size M" rather than reading a whole fit table aloud
		// every time a field changes. The detail sits below, outside it.
		//
		// Both live in one .haki-sg__rec-answer box because above 990px the panel
		// is two columns — form on the left, the whole answer on the right. Kept as
		// two loose siblings the grid would place them in separate cells, and
		// `:empty { display: none }` on the live region would then slide the detail
		// up into the headline's slot the moment there was no headline.
		var answer = el('div', 'haki-sg__rec-answer');
		var live = el('div', 'haki-sg__rec-live');
		live.setAttribute('role', 'status');
		live.setAttribute('aria-live', 'polite');
		var detail = el('div', 'haki-sg__rec-detail');
		answer.appendChild(live);
		answer.appendChild(detail);
		wrap.appendChild(answer);

		// The answer column would otherwise open as an empty half of the panel.
		// Rendered into `detail` rather than the live region so a screen reader
		// does not announce it on arrival; the first render clears it.
		detail.appendChild(
			textNode('p', 'haki-sg__hint', 'Fill in the form and your size appears here.')
		);

		/* ---- restore saved answers ----------------------------------------- */
		function applyUnit(field, unit) {
			if (unit) field.setUnit(unit, false);
		}
		applyUnit(height, savedUnits.height);
		applyUnit(weight, savedUnits.weight);
		if (typeof saved.heightCm === 'number' && isFinite(saved.heightCm)) {
			if (height.getUnit() === 'ft_in') {
				var totalIn = saved.heightCm / CM_PER_IN;
				height.input.value = String(Math.floor(totalIn / 12));
				height.input2.value = String(round1(totalIn % 12));
			} else {
				height.input.value = String(round1(saved.heightCm));
			}
		}
		if (typeof saved.weightKg === 'number' && isFinite(saved.weightKg)) {
			weight.input.value = String(
				round1(weight.getUnit() === 'lb' ? saved.weightKg / HS.KG_PER_LB : saved.weightKg)
			);
		}
		if (typeof saved.ageYears === 'number' && isFinite(saved.ageYears)) {
			age.input.value = String(saved.ageYears);
		}
		canonicals.forEach(function (canonical) {
			var field = measureFields[canonical];
			applyUnit(field, savedUnits.measure);
			var cm = savedMeasurements[canonical];
			if (typeof cm === 'number' && isFinite(cm)) {
				field.input.value = String(round1(field.getUnit() === 'cm' ? cm : cm / CM_PER_IN));
			}
		});

		/* ---- unit toggles convert what is already typed --------------------- */
		height.onUnitChange = function (from, to) {
			var cm = readHeightCm(from);
			if (cm === null) return;
			if (to === 'ft_in') {
				var totalIn = cm / CM_PER_IN;
				height.input.value = String(Math.floor(totalIn / 12));
				height.input2.value = String(round1(totalIn % 12));
			} else {
				height.input.value = String(round1(cm));
				height.input2.value = '';
			}
		};
		weight.onUnitChange = function (from, to) {
			var parsed = parseWeight(weight.input.value, from);
			if (!parsed) return;
			var kg = HS.weightToKg(
				parsed.unit === 'kg' ? {unit: 'kg', kg: parsed.value} : {unit: 'lb', lb: parsed.value}
			);
			weight.input.value = String(round1(to === 'lb' ? kg / HS.KG_PER_LB : kg));
		};
		canonicals.forEach(function (canonical) {
			var field = measureFields[canonical];
			field.onUnitChange = function (from, to) {
				var cm = HS.parseBodyInput(field.input.value, from);
				if (cm === null) return;
				field.input.value = String(round1(to === 'cm' ? cm : cm / CM_PER_IN));
			};
		});

		function readHeightCm(unitOverride) {
			var unit = unitOverride || height.getUnit();
			if (unit === 'ft_in') {
				var feet = plainNumber(height.input.value);
				var inches = plainNumber(height.input2.value);
				if (feet === null && inches === null) return null;
				return HS.heightToCm({unit: 'ft_in', feet: feet || 0, inches: inches || 0});
			}
			// parseBodyInput, not plainNumber: someone typing "69in" into the cm
			// box means inches, and the engine already knows that rule.
			return HS.parseBodyInput(height.input.value, 'cm');
		}

		/* ---- validation ------------------------------------------------------ */
		function fmtLen(cm) {
			return state.unit === 'cm'
				? round1(cm) + ' cm'
				: round1(cm / CM_PER_IN) + ' in';
		}

		function heightRangeText() {
			if (height.getUnit() === 'ft_in') {
				var lo = HS.VALID_HEIGHT_CM.min / CM_PER_IN;
				var hi = HS.VALID_HEIGHT_CM.max / CM_PER_IN;
				return (
					Math.floor(lo / 12) + 'ft ' + Math.round(lo % 12) + 'in and ' +
					Math.floor(hi / 12) + 'ft ' + Math.round(hi % 12) + 'in'
				);
			}
			return HS.VALID_HEIGHT_CM.min + 'cm and ' + HS.VALID_HEIGHT_CM.max + 'cm';
		}

		function weightRangeText() {
			if (weight.getUnit() === 'lb') {
				return (
					Math.round(HS.VALID_WEIGHT_KG.min / HS.KG_PER_LB) + 'lb and ' +
					Math.round(HS.VALID_WEIGHT_KG.max / HS.KG_PER_LB) + 'lb'
				);
			}
			return HS.VALID_WEIGHT_KG.min + 'kg and ' + HS.VALID_WEIGHT_KG.max + 'kg';
		}

		function clearErrors() {
			height.setError(null);
			weight.setError(null);
			age.setError(null);
			canonicals.forEach(function (c) {
				measureFields[c].setError(null);
			});
		}

		/**
		 * Collect the form into a RecommenderInput, or return the first problem.
		 * A bad number is always named — the failure mode this replaces is a
		 * widget that quietly returns nothing and leaves the shopper guessing
		 * whether it is broken or they are unsizeable.
		 */
		function collect() {
			clearErrors();
			var input = {mode: mode, unitSystem: state.unit === 'cm' ? 'metric' : 'imperial'};

			if (fit) input.fitPreference = fit.value();

			var filters = {};
			filterSelects.forEach(function (f) {
				if (f.select.value) filters[f.columnIndex] = f.select.value;
			});
			input.filters = filters;

			if (mode === 'simple') {
				var cm = readHeightCm(null);
				if (cm === null) {
					height.setError('Enter your height.');
					return {error: 'Enter your height to get a size.', focus: height.input};
				}
				if (cm < HS.VALID_HEIGHT_CM.min || cm > HS.VALID_HEIGHT_CM.max) {
					var hm = 'That height looks off - this estimate works between ' + heightRangeText() + '.';
					height.setError(hm);
					return {error: hm, focus: height.input};
				}
				var w = parseWeight(weight.input.value, weight.getUnit());
				if (!w) {
					weight.setError('Enter your weight.');
					return {error: 'Enter your weight to get a size.', focus: weight.input};
				}
				var kg = HS.weightToKg(
					w.unit === 'kg' ? {unit: 'kg', kg: w.value} : {unit: 'lb', lb: w.value}
				);
				if (kg < HS.VALID_WEIGHT_KG.min || kg > HS.VALID_WEIGHT_KG.max) {
					var wm = 'That weight looks off - this estimate works between ' + weightRangeText() + '.';
					weight.setError(wm);
					return {error: wm, focus: weight.input};
				}
				var years = null;
				if (String(age.input.value).trim()) {
					years = plainNumber(age.input.value);
					if (
						years === null ||
						years < HS.VALID_AGE_YEARS.min ||
						years > HS.VALID_AGE_YEARS.max
					) {
						var am =
							'This estimate is built on adult measurements - enter an age between ' +
							HS.VALID_AGE_YEARS.min + ' and ' + HS.VALID_AGE_YEARS.max + ', or leave it blank.';
						age.setError(am);
						return {error: am, focus: age.input};
					}
				}
				input.demographics = {sex: sex.value() === 'female' ? 'female' : 'male', heightCm: cm, weightKg: kg};
				if (years !== null) input.demographics.ageYears = years;
				return {
					input: input,
					save: {
						mode: 'simple',
						sex: input.demographics.sex,
						heightCm: cm,
						weightKg: kg,
						ageYears: years,
						fitPreference: input.fitPreference || null,
						units: {height: height.getUnit(), weight: weight.getUnit(), measure: savedUnits.measure || 'in'}
					}
				};
			}

			var measurements = {};
			var any = false;
			var bad = null;
			canonicals.forEach(function (canonical) {
				var field = measureFields[canonical];
				var raw = String(field.input.value).trim();
				if (!raw) return;
				var value = HS.parseBodyInput(raw, field.getUnit());
				if (value === null || !(value > 0)) {
					var msg = 'That doesn’t look like a measurement. Try “31”, “31 1/2” or “80cm”.';
					field.setError(msg);
					if (!bad) bad = {error: msg, focus: field.input};
					return;
				}
				measurements[canonical] = value;
				any = true;
			});
			if (bad) return bad;
			var filterAnswered = false;
			for (var fk in filters) {
				if (Object.prototype.hasOwnProperty.call(filters, fk)) filterAnswered = true;
			}
			// A filter answer alone is a complete question on a lookup table, so it
			// only counts as "nothing entered" when there is no filter either.
			if (!any && !filterAnswered) {
				if (!canonicals.length) {
					return {
						error: 'Pick an option above to see which size matches.',
						focus: filterSelects.length ? filterSelects[0].select : null
					};
				}
				var first = measureFields[canonicals[0]];
				return {
					error: 'Enter at least one measurement - even just your ' +
						(CANON_LABELS[canonicals[0]] || canonicals[0]).toLowerCase() + ' is enough.',
					focus: first ? first.input : null
				};
			}
			input.measurements = measurements;
			var mergedUnits = {
				height: height.getUnit(),
				weight: weight.getUnit(),
				measure: canonicals.length ? measureFields[canonicals[0]].getUnit() : savedUnits.measure || 'in'
			};
			// Saved by CANONICAL name, so a merchant renaming or fixing a typo in a
			// column header never loses what the shopper measured.
			var mergedMeasurements = {};
			for (var k in savedMeasurements) {
				if (Object.prototype.hasOwnProperty.call(savedMeasurements, k)) {
					mergedMeasurements[k] = savedMeasurements[k];
				}
			}
			for (var k2 in measurements) {
				if (Object.prototype.hasOwnProperty.call(measurements, k2)) {
					mergedMeasurements[k2] = measurements[k2];
				}
			}
			return {
				input: input,
				save: {
					mode: 'measurements',
					measurements: mergedMeasurements,
					fitPreference: input.fitPreference || null,
					units: mergedUnits
				}
			};
		}

		/* ---- rendering the answer -------------------------------------------- */
		function clearOut(node) {
			while (node.firstChild) node.removeChild(node.firstChild);
		}

		function renderError(message) {
			clearOut(live);
			clearOut(detail);
			var box = el('div', 'haki-sg__rec-message');
			box.appendChild(textNode('p', 'haki-sg__rec-message-text', message));
			live.appendChild(box);
			highlightSizeOption(null);
		}

		function renderWarnings(target, warnings) {
			if (!warnings || !warnings.length) return;
			var list = el('ul', 'haki-sg__rec-warnings');
			warnings.forEach(function (w) {
				list.appendChild(textNode('li', null, w));
			});
			target.appendChild(list);
		}

		function renderFitTable(best) {
			var rows = best.measurements.filter(function (m) {
				return m.garmentCm !== null || m.scored;
			});
			if (!rows.length) return null;

			var wrapper = el('div', 'haki-sg__table-wrap');
			wrapper.setAttribute('tabindex', '0');
			wrapper.setAttribute('role', 'group');
			wrapper.setAttribute('aria-label', 'How size ' + best.size + ' fits you');

			var table = el('table', 'haki-sg__table haki-sg__rec-fit');
			var caption = textNode('caption', 'haki-sg__rec-caption', 'How size ' + best.size + ' fits you');
			table.appendChild(caption);

			var thead = el('thead', null);
			var hr = el('tr', null);
			['Measurement', 'This size', 'You', 'Fit'].forEach(function (t) {
				var th = textNode('th', null, t);
				th.setAttribute('scope', 'col');
				hr.appendChild(th);
			});
			thead.appendChild(hr);
			table.appendChild(thead);

			var tbody = el('tbody', null);
			rows.forEach(function (m) {
				var tr = el('tr', null);
				var th = textNode('th', null, m.label);
				th.setAttribute('scope', 'row');
				tr.appendChild(th);
				tr.appendChild(textNode('td', null, m.garmentDisplay || '-'));
				tr.appendChild(textNode('td', null, m.bodyCm === null ? '-' : fmtLen(m.bodyCm)));
				tr.appendChild(
					textNode(
						'td',
						'haki-sg__verdict',
						m.verdict === null ? 'not compared' : HS.verdictLabel(m.verdict, m.kind)
					)
				);
				tbody.appendChild(tr);
			});
			table.appendChild(tbody);
			wrapper.appendChild(table);
			return wrapper;
		}

		function render(rec) {
			clearOut(live);
			clearOut(detail);

			if (rec.status === 'not_configured' || rec.status === 'no_input') {
				var box = el('div', 'haki-sg__rec-message');
				box.appendChild(
					textNode(
						'p',
						'haki-sg__rec-message-text',
						rec.status === 'no_input'
							? rec.reason || 'Fill in the form above and we’ll work out your size.'
							: 'This chart isn’t set up to recommend a size yet - use the size chart tab instead.'
					)
				);
				live.appendChild(box);
				renderWarnings(detail, rec.warnings);
				highlightSizeOption(null);
				return;
			}

			if (rec.status === 'no_fit') {
				var nf = el('div', 'haki-sg__rec-message');
				nf.appendChild(
					textNode(
						'p',
						'haki-sg__rec-message-text',
						'Nothing in this chart will fit you. We’d rather say so than guess.'
					)
				);
				// The engine's reason names the likely CAUSE, not just the symptom.
				if (rec.reason) nf.appendChild(textNode('p', 'haki-sg__rec-why', rec.reason));
				live.appendChild(nf);
				renderWarnings(detail, rec.warnings);
				highlightSizeOption(null);
				return;
			}

			var best = rec.best;
			var headline = el('div', 'haki-sg__rec-headline');
			headline.appendChild(textNode('p', 'haki-sg__rec-eyebrow', 'Your size'));
			headline.appendChild(textNode('p', 'haki-sg__rec-size', best.size));
			if (rec.bodySource === 'estimated') {
				headline.appendChild(
					textNode('p', 'haki-sg__rec-estimate', 'Estimated from your height and weight.')
				);
			}
			live.appendChild(headline);

			if (rec.runnerUp) {
				detail.appendChild(
					textNode(
						'p',
						'haki-sg__rec-runnerup',
						'Next closest: size ' + rec.runnerUp.size + ' (' + rec.runnerUp.scorePercent + '% match).'
					)
				);
			} else {
				detail.appendChild(
					textNode('p', 'haki-sg__rec-runnerup', 'No other size on this chart came close.')
				);
			}

			var fitTable = renderFitTable(best);
			if (fitTable) detail.appendChild(fitTable);

			renderWarnings(detail, rec.warnings);

			if (highlightSizeOption(best.size)) {
				detail.appendChild(
					textNode(
						'p',
						'haki-sg__hint',
						'Size ' + best.size + ' is highlighted in the size picker behind this window. Nothing has been selected for you.'
					)
				);
			}
		}

		var hasRun = false;

		function run(focusOnError) {
			var collected = collect();
			if (collected.error) {
				renderError(collected.error);
				if (focusOnError && collected.focus) collected.focus.focus();
				hasRun = true;
				return;
			}
			var rec;
			try {
				rec = HS.recommendSize(section, config, collected.input);
			} catch (e) {
				// The engine is total by contract; if that ever stops being true the
				// shopper gets a sentence, not a broken tab.
				renderError('We couldn’t work that out. Use the size chart tab instead.');
				return;
			}
			hasRun = true;
			saveAnswers(collected.save);
			render(rec);
		}

		form.addEventListener('submit', function (event) {
			event.preventDefault();
			run(true);
		});
		// Re-run on a settled change (radio, select, or a text input on blur), but
		// only once the shopper has asked for an answer — recomputing under someone
		// who has not finished typing would announce half-finished results.
		form.addEventListener('change', function () {
			if (hasRun) run(false);
		});

		return {
			node: wrap,
			refresh: function () {
				if (hasRun) run(false);
			}
		};
	}

	/* ---------------------------------------------------------------- boot  */

	/* --------------------------------------------- breakpoint column order */

	/*
	 * Which of the two column groups comes first.
	 *
	 * Owner, testing on his phone: "reorder the layout so the chart image is the
	 * first thing that loads, THEN the actual measurement chart". Stacked in one
	 * column the diagram should lead, because it is what the table's column
	 * headers and the numbered instructions ("Body Length (#1)…") both refer to;
	 * reading a Chest Width figure before you have seen where chest width is
	 * measured is backwards. Side by side at 990px+ that argument disappears —
	 * the diagram is already in view next to the table — and the wide layout
	 * stays as it is: main column (tables, text, dividers) left, diagrams right.
	 *
	 * DONE BY MOVING A NODE, NOT BY CSS `order` / `column-reverse`. DOM order is
	 * reading order in this modal at every width, deliberately (see the "desktop
	 * two columns" block in haki-size-guide.css). A visual-only reorder would
	 * leave a screen reader and the tab sequence announcing the table first while
	 * the eye sees the diagram first — the WCAG 1.3.2 mismatch scripts/a11y is
	 * there to catch — and would read out "#1", "#2" callouts for a picture the
	 * user has not been given yet.
	 *
	 * Because the arrangement now depends on the viewport it has to be re-derived
	 * when the viewport crosses the breakpoint: matchMedia on the SAME query
	 * string the stylesheet uses, so the two cannot drift, rather than a resize
	 * handler that fires hundreds of times and has to re-measure.
	 *
	 * Nothing is rebuilt. The two column elements are moved with their subtrees
	 * intact, so the unit toggle's state, the live cell list, the active tab, the
	 * recommender's typed answers and every event listener survive untouched —
	 * the recommender is not even inside this wrapper. The single casualty of
	 * moving a node is focus (a detached element is blurred), so it is saved and
	 * restored below.
	 */
	var TWO_COLUMN_QUERY = 'screen and (min-width: 990px)';

	function orderColumns(cols, mainCol, asideCol) {
		var mq = typeof window.matchMedia === 'function' ? window.matchMedia(TWO_COLUMN_QUERY) : null;

		function apply() {
			// No matchMedia at all: keep the wide order, which is also the payload's
			// authored order. Never worse than what shipped before this existed.
			var first = (mq ? mq.matches : true) ? mainCol : asideCol;
			// Already right — and this is the branch every desktop load takes, so
			// the two-column arrangement is not merely equivalent, it is untouched.
			if (cols.firstChild === first) return;

			var active = document.activeElement;
			var refocus = active && active !== document.body && cols.contains(active) ? active : null;
			var selStart = null;
			var selEnd = null;
			if (refocus) {
				try {
					selStart = refocus.selectionStart;
					selEnd = refocus.selectionEnd;
				} catch (e) {
					// selectionStart throws on input types that have no selection
				}
			}

			// Exactly two children, so moving one of them IS the reorder.
			cols.insertBefore(first, cols.firstChild);

			if (refocus && document.body.contains(refocus)) {
				try {
					refocus.focus({preventScroll: true});
					if (typeof selStart === 'number') refocus.setSelectionRange(selStart, selEnd);
				} catch (e) {
					/* focus is a nicety here; never let it break the layout */
				}
			}
		}

		apply();

		if (!mq) return;
		if (typeof mq.addEventListener === 'function') mq.addEventListener('change', apply);
		else if (typeof mq.addListener === 'function') mq.addListener(apply); // Safari < 14
	}

	/* -------------------------------- instruction box: height + affordance */

	/*
	 * Ends each measuring-instructions box level with the bottom of the diagram it
	 * belongs to, so a garment's group reads as one rectangle — table and
	 * instructions on the left, diagram on the right, both columns finishing at
	 * about the same line — instead of the left column running a long way past the
	 * right. The frame, the scrolling and the fade are all CSS; the ONE thing that
	 * cannot be expressed there is the number, and the long note on
	 * .haki-sg__howto in haki-size-guide.css sets out why (short version: a grid
	 * row is as tall as its tallest cell, and the cell we want to constrain is the
	 * one making the row tall).
	 *
	 * PAIRING is by payload order, not by index: a box takes the last diagram
	 * authored ABOVE it, which for the flattened bundles the PLM composer emits
	 * (divider, table, diagram, instructions, divider, table, diagram,
	 * instructions) is that garment's own diagram. A box with no diagram above it
	 * — a chart that puts its prose first — falls back to the first diagram in the
	 * chart, which is still that box's diagram on any single-garment layout.
	 *
	 * That pairing is also what keeps the two halves of a bundle in step without
	 * anything measuring "the column" as a whole: capping the hoodie's box to the
	 * hoodie diagram's bottom is what puts the pants group's top level with the
	 * pants diagram's top, and so on down.
	 */

	// Roughly five lines plus the padding. THE FLOOR MATTERS: when the table alone
	// is already taller than the diagram beside it there is no room left, and a
	// cap computed honestly would be zero or negative — a box collapsed to a
	// hairline with the instructions hidden inside it. Better that the group grows
	// past the diagram, which is what happened before this existed.
	var HOWTO_MIN_HEIGHT = 132;
	// .haki-sg__howto's own 1px top and bottom edge. The cap lands on the scroller
	// INSIDE that frame, so the frame's own two pixels come off the available room
	// or every box would finish 2px below its diagram.
	var HOWTO_FRAME = 2;
	// Two lines of body copy (13px / 1.65). DON'T CAP A BOX THAT IS BARELY OVER —
	// the box's own 13px of side padding narrows the measure enough to push the
	// commonest chart's instructions onto one extra line, and at 1440x900 that was
	// enough on its own to turn the ordinary tee guide into a scroll pane hiding a
	// line and a half. A group finishing 30px below its diagram is invisible; a
	// scrollbar that exists to hide one line is not. Over the grace band the cap
	// applies in full — this buys a little slack, it does not soften the cap.
	var HOWTO_GRACE = 44;

	function fitInstructionBoxes(modal, boxes) {
		if (!boxes.length) return {schedule: function () {}};
		var mq = typeof window.matchMedia === 'function' ? window.matchMedia(TWO_COLUMN_QUERY) : null;
		var queued = false;

		function fade(box) {
			// >1 rather than >0: sub-pixel line heights leave a fractional remainder
			// on a box that is, to the eye, scrolled all the way down.
			var s = box.scroller;
			var more = s.scrollHeight - s.scrollTop - s.clientHeight > 1;
			box.node.classList.toggle('hk-sg-more', more);
		}

		function measure() {
			queued = false;
			// A section deleted in the theme editor leaves this listener behind on
			// window; do not measure a modal that is no longer on the page.
			if (!document.body.contains(modal)) return;
			// Every rect inside a display:none modal is zero, and a cap of zero is a
			// box with the instructions sealed inside it. Nothing to do until it opens.
			if (!modal.hasAttribute('open')) return;

			if (mq && !mq.matches) {
				// Stacked. The stylesheet ignores the cap below the breakpoint anyway,
				// but a stale .hk-sg-more would leave a fade painted over a box that
				// cannot scroll.
				boxes.forEach(function (box) {
					box.scroller.style.removeProperty('--hk-sg-howto-cap');
					box.node.classList.remove('hk-sg-more');
				});
				return;
			}

			// Start from the untruncated layout rather than from the last viewport's
			// answer.
			boxes.forEach(function (box) {
				box.cap = null;
				box.scroller.style.removeProperty('--hk-sg-howto-cap');
			});

			// One sweep. In DOM order, and one box at a time, because capping a box
			// moves every box below it up the page: reading offsetTop is what flushes
			// the previous box's write, so each is measured against positions the
			// ones above it have already settled into. Two or three boxes is the
			// whole range here (a bundle is two garments), so the synchronous reflows
			// that costs are not worth avoiding.
			function sweep() {
				var moved = false;
				boxes.forEach(function (box) {
					if (!box.image || !box.node.offsetParent) return;
					// offsetTop/offsetHeight, NOT getBoundingClientRect(). The panel
					// plays a `scale: 0.98` -> 1 enter animation, and rects are reported
					// AFTER transforms — the first measurement after open would be ~2%
					// short and would keep moving for 340ms. Offsets are layout values:
					// no transforms, and no dependence on how far .haki-sg__body happens
					// to be scrolled. Both elements resolve against the same
					// offsetParent (.haki-sg__content, the nearest positioned ancestor
					// of either column), so the subtraction is meaningful.
					var room =
						box.image.offsetTop + box.image.offsetHeight - box.node.offsetTop - HOWTO_FRAME;
					// scrollHeight is the FULL content height whether or not a cap is
					// currently applied, so this reads the same on every sweep.
					var natural = box.scroller.scrollHeight;
					// 0 means "no cap at all" — a real cap can never be 0, because of
					// the floor above.
					var cap =
						natural - room > HOWTO_GRACE ? Math.max(HOWTO_MIN_HEIGHT, room) : 0;
					if (box.cap !== null && Math.abs(cap - box.cap) <= 1) return;
					box.cap = cap;
					if (cap) box.scroller.style.setProperty('--hk-sg-howto-cap', cap + 'px');
					else box.scroller.style.removeProperty('--hk-sg-howto-cap');
					moved = true;
				});
				return moved;
			}

			// SWEEP TWICE, at least. Capping a box can shorten the panel enough that
			// .haki-sg__body stops needing a scrollbar of its own; the 15px that hands
			// back widens BOTH grid tracks, and a square diagram in a wider track is a
			// taller diagram — so the room the first sweep measured is no longer the
			// room there is. Measured on the long-instructions case at 1920: the box
			// finished 6px above its diagram until this loop existed. Three is the
			// ceiling and it has never been reached; the exit is "nothing moved by
			// more than a pixel", not the count.
			for (var i = 0; i < 3 && sweep(); i++) {
				/* the condition is the work */
			}

			boxes.forEach(fade);
		}

		function schedule() {
			if (queued) return;
			queued = true;
			if (typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(measure);
			else window.setTimeout(measure, 16);
		}

		// Opening is the first moment there is anything to measure. Its own
		// observer rather than a hook into watchOpenState(): that function is the
		// scroll lock and the exit animation, and this has no business being able
		// to break either.
		if (typeof window.MutationObserver === 'function') {
			new window.MutationObserver(function () {
				if (modal.hasAttribute('open')) schedule();
			}).observe(modal, {attributes: true, attributeFilter: ['open']});
		}

		// Both column widths, and therefore the square diagram's height, are a
		// function of the viewport; so is the 58vh cap on the diagram itself.
		window.addEventListener('resize', schedule);

		boxes.forEach(function (box) {
			box.scroller.addEventListener('scroll', function () {
				fade(box);
			});
			// The diagrams carry width/height attributes, so the box is reserved at
			// the authored 1024-square before the file arrives — but that is a HINT,
			// and the real ratio replaces it on decode. Re-measure when it does.
			if (box.image && !box.image.complete) {
				box.image.addEventListener('load', schedule);
				box.image.addEventListener('error', schedule);
			}
		});

		// A late webfont swap changes how many lines the instructions take.
		if (document.fonts && typeof document.fonts.ready === 'object') {
			try {
				document.fonts.ready.then(schedule);
			} catch (e) {
				/* nothing to do: the open/resize passes still measure */
			}
		}

		return {schedule: schedule};
	}

	/*
	 * The other half of "the first thing that loads".
	 *
	 * The diagrams ship `loading="lazy"`, and the modal is `display: none` until
	 * it opens — a lazy image with no layout box is never near the viewport, so
	 * the fetch does not even START until the shopper taps. That was tolerable
	 * while the diagram sat below the table; now that it leads the phone layout it
	 * would be a ~400x400 blank square at the top of the panel with the chart
	 * pushed below it, which is worse than the order we just changed away from.
	 *
	 * Eager would fix it by making every PDP visitor download a diagram most of
	 * them never open — 200-780KB of nothing on the store's most-visited template.
	 * So: warm on INTENT instead. Hovering, focusing or first touching the trigger
	 * flips the diagrams to eager, which per the HTML spec starts the fetch
	 * immediately even though the element is still display:none. A touchstart
	 * lands well before the click it precedes, so the phone case — the one the
	 * owner is looking at — gets the head start, and a shopper who never goes near
	 * the button still pays nothing.
	 */
	function warmDiagrams(trigger, images) {
		if (!images || !images.length) return;
		var warmed = false;

		function warm() {
			if (warmed) return;
			warmed = true;
			Array.prototype.forEach.call(images, function (img) {
				img.setAttribute('loading', 'eager');
			});
		}

		// focusin, not focus: focus does not bubble and these listeners sit on the
		// wrapper, not on the button inside it.
		['pointerenter', 'touchstart', 'focusin'].forEach(function (type) {
			trigger.addEventListener(type, warm, true);
		});
	}

	/* --------------------------------- open state: lock, page-shift, exit  */

	/*
	 * Three jobs, one observer, and they have to happen in this order.
	 *
	 * JOB 1 — lock the page. <modal-dialog>.show() (assets/global.js) adds
	 * `overflow-hidden` to <body>, base.css defines that class, and the whole
	 * thing does NOTHING on this store: `overflow` on <body> only reaches the
	 * viewport by propagation, and propagation is switched off unless the ROOT
	 * element's overflow is `visible` in both axes (css-overflow-3 §3.3).
	 * layout/theme.liquid sets `html { overflow-x: clip }` — the "sticky guard" —
	 * so the root is `clip visible`, body.overflow-hidden merely makes <body> its
	 * own (never-overflowing, auto-height) scroll container, and the document
	 * carries on scrolling behind the modal. That is the reported bug, and it is
	 * why this cannot be fixed by "adding the class properly": the class was
	 * always there.
	 *
	 * So lock the element that really is the scroller, exactly as the cart drawer
	 * does (`html.hk-cart-open` in haki-cart.css). The rules live in
	 * haki-size-guide.css under .haki-sg-locked, on a class this file owns —
	 * every other overlay sharing body.overflow-hidden is left alone.
	 *
	 * JOB 2 — pay back the page shift that JOB 1 causes. Locking the root drops
	 * the classic scrollbar and, with it, the stable gutter theme.liquid reserves
	 * above 750px; the viewport then grows by the scrollbar width and every
	 * %-width element on the page behind re-lays-out (the title and price slide
	 * right, the product images shrink).
	 *
	 * DON'T copy the cart/search idiom here (haki-cart.css,
	 * sections/haki-header.liquid), which compensates by a scrollbar width it
	 * assumes. Above 990px theme.liquid hides the scrollbar outright
	 * (`scrollbar-width: none`), so there is nothing to pay back, and paying 15px
	 * anyway INTRODUCES a 15px shift. (Measured: it did exactly that on the first
	 * attempt at this fix.)
	 *
	 * What is correct is to compensate for the movement that HAPPENED: sample
	 * either side of the lock and hand back the difference. Which is why the
	 * class goes on BEFORE the widths are read below — that read is what forces
	 * the layout the lock just invalidated, and it IS the measurement. Where
	 * nothing moved the difference is 0, .haki-sg-open is never added, and the
	 * compensation is a no-op.
	 *
	 * MEASURE THE BOXES BEING CORRECTED, not a proxy for them. This is the whole
	 * lesson of the bug that got here: documentElement.clientWidth is neither of
	 * them. At 800px (the 750-990 band, classic scrollbar forced on) it read
	 * 785 -> 800 across the lock, because it excludes a RENDERED SCROLLBAR and
	 * the scrollbar stopped being rendered. But Chromium keeps the stable gutter
	 * under `overflow: hidden`, so the two boxes that actually matter did not
	 * move at all: body.clientWidth stayed 785, and the fixed header's right edge
	 * stayed 785. Handing that phantom 15px back as padding did not correct a
	 * shift, it CREATED one — body 785 -> 770, the whole column jumping left.
	 *
	 * So: page content is measured by body's own width, and the fixed header by
	 * its own right edge. A fixed element is laid out against the viewport rather
	 * than <html>'s content box, so html padding cannot reach it and it needs its
	 * own number; and each number now comes from the element it is spent on, so
	 * neither can be a phantom. Above 990px theme.liquid hides the scrollbar
	 * outright (`scrollbar-width: none`), both deltas are 0 and nothing is
	 * applied — that is the owner's desktop and the common case.
	 *
	 * JOB 3 — hold the modal on screen long enough to play its exit animation, and
	 * only THEN undo jobs 1 and 2. See the "open / close animation" block in
	 * haki-size-guide.css for the treatment itself and for why the ENTER half needs
	 * no JavaScript at all; what needs JavaScript is the exit, because
	 * <modal-dialog>.hide() (assets/global.js — shared by every modal in the theme
	 * and therefore not ours to change) clears [open] the instant you hit Escape,
	 * the X or the backdrop, and without [open] the modal is `display: none`.
	 *
	 * That is also why the exit lives in THIS state machine rather than one of its
	 * own: releasing the lock is not something that happens at "close", it is
	 * something that happens at the END of the exit. Unlock when [open] goes and
	 * the scrollbar comes back, .haki-sg-open's compensation padding comes off, and
	 * the page behind re-lays-out in plain view through a scrim that is still
	 * fading. Two state machines would have to agree about that ordering; one
	 * cannot disagree with itself.
	 *
	 * A MutationObserver callback is a MICROTASK — it runs after the click handler
	 * that called hide() and before the browser's next rendering opportunity — so
	 * adding the closing class in sync() means there is never a painted frame in
	 * which the modal is display:none. That timing IS the mechanism: a
	 * requestAnimationFrame or a setTimeout(0) here would be one frame too late and
	 * the modal would blink out before it could animate.
	 */
	var CLOSING_CLASS = 'haki-sg--closing';

	/*
	 * How long the exit really lasts, read back off the element that is playing it
	 * instead of hardcoded here. The stylesheet owns the number (--hk-sg-out ->
	 * --duration-default -> 250ms); a second copy in this file is a copy that can
	 * drift, and drifting SHORT means tearing the modal down mid-fade.
	 *
	 * Returns 0 when nothing will actually animate — "tear down now", never "wait
	 * and see". animation-NAME is checked and not merely the duration, because
	 * base.css's global reduced-motion rule pins `animation-duration: .01ms
	 * !important`: under prefers-reduced-motion our own media query sets
	 * `animation: none`, but the duration would still read as a real, tiny
	 * animation — one that never fires an animationend, because it never runs.
	 */
	function exitDuration(node) {
		var style;
		try {
			style = window.getComputedStyle(node);
		} catch (e) {
			return 0;
		}
		if (!style || !style.animationName || style.animationName === 'none') return 0;
		var parts = String(style.animationDuration || '').split(',');
		var longest = 0;
		for (var i = 0; i < parts.length; i++) {
			var raw = parts[i].trim();
			var n = parseFloat(raw);
			if (!isFinite(n)) continue;
			// 'ms' first: '0.25s'.slice(-2) is '5s', so testing for 's' would misread it.
			var ms = raw.slice(-2) === 'ms' ? n : n * 1000;
			if (ms > longest) longest = ms;
		}
		return longest;
	}

	function watchOpenState(trigger, modal) {
		var root = document.documentElement;
		// On screen and holding the page: [open], OR playing the exit animation.
		var rendered = false;
		// Exit animation in flight right now.
		var closing = false;
		var closeTimer = null;
		var exitNode = null;
		var onExitEnd = null;
		var contentBefore = null;
		var headerBefore = null;

		function headerRight() {
			var header = document.querySelector('.haki-header');
			return header ? header.getBoundingClientRect().right : null;
		}

		// The pre-lock samples have to be taken on the way in: the observer below
		// is a microtask and only runs once show() has already locked. Capture
		// phase on the wrapper beats <modal-opener>'s own bubble-phase listener on
		// the button inside it. Keyboard activation dispatches a click too, so
		// Enter/Space are covered.
		trigger.addEventListener(
			'click',
			function () {
				contentBefore = document.body.clientWidth;
				headerBefore = headerRight();
			},
			true
		);

		// JOBS 1 + 2, on the way in.
		function lockPage() {
			// JOB 1. Unconditional — the lock is the whole point and must not be
			// contingent on the measurement below succeeding.
			root.classList.add('haki-sg-locked');

			// JOB 2. No pre-lock sample means the modal was opened by something
			// other than the trigger; measuring nothing is better than compensating
			// by a guess, and an uncompensated lock is still a working lock.
			if (contentBefore === null) return;
			// Read both before writing either: setting a custom property that a
			// rule consumes invalidates layout and would poison the second read.
			var contentGrew = document.body.clientWidth - contentBefore;
			var headerAfter = headerRight();
			var headerGrew = headerBefore === null || headerAfter === null
				? 0
				: Math.round(headerAfter - headerBefore);
			contentBefore = null;
			headerBefore = null;
			if (contentGrew <= 0 && headerGrew <= 0) return; // nothing moved

			if (contentGrew > 0) root.style.setProperty('--hk-sg-content-comp', contentGrew + 'px');
			if (headerGrew > 0) root.style.setProperty('--hk-sg-header-comp', headerGrew + 'px');
			root.classList.add('haki-sg-open');
		}

		// ...and the exact inverse, in one place, so no caller can undo half of it.
		// (The old copy of this removed a `--hk-sg-shift-comp` that no rule has read
		// since the compensation was split into content/header — the two live custom
		// properties were being left on <html> after every close.)
		function releasePage() {
			root.classList.remove('haki-sg-locked');
			root.classList.remove('haki-sg-open');
			root.style.removeProperty('--hk-sg-content-comp');
			root.style.removeProperty('--hk-sg-header-comp');
		}

		// Leave the exit phase without deciding WHY. Two callers with opposite
		// intentions — the exit finished, or it was interrupted by a re-open — and
		// the teardown they share is identical; only what happens to the page lock
		// afterwards differs, so that is left to them.
		function endExitPhase() {
			closing = false;
			if (closeTimer !== null) {
				window.clearTimeout(closeTimer);
				closeTimer = null;
			}
			if (exitNode && onExitEnd) {
				exitNode.removeEventListener('animationend', onExitEnd);
				exitNode.removeEventListener('animationcancel', onExitEnd);
			}
			exitNode = null;
			onExitEnd = null;
			modal.classList.remove(CLOSING_CLASS);
			// Reversing this in every exit path, including the interrupted one, is
			// what stops a missed teardown leaving a permanently dead modal.
			modal.inert = false;
		}

		function finishClose() {
			if (!closing) return;
			endExitPhase();
			rendered = false;
			releasePage();
		}

		function beginClose() {
			closing = true;
			modal.classList.add(CLOSING_CLASS);
			// Focus has already gone back to the trigger (global.js's hide() calls
			// removeTrapFocus(this.openedBy) synchronously). `inert` covers the rest
			// of the beat: no tabbing back into a dialog that is on its way out, and
			// nothing left for a screen reader to wander into. Ignored by browsers
			// without it, which simply behave as they do today.
			modal.inert = true;

			// The panel outlasts the scrim (0.25s vs 0.25s reversed — equal today,
			// but the panel is the one carrying the movement), so it is the panel
			// that says when the exit is over.
			exitNode = modal.querySelector('.haki-sg__content');
			var ms = exitNode ? exitDuration(exitNode) : 0;
			if (!ms) {
				// Reduced motion, or no animation reached us at all. Nothing to wait
				// for: close exactly as this modal did before it had an exit.
				finishClose();
				return;
			}

			onExitEnd = function (event) {
				// animationend bubbles; only the panel's own is the finish line.
				if (event.target !== exitNode) return;
				finishClose();
			};
			exitNode.addEventListener('animationend', onExitEnd);
			// A cancelled animation (the node reparented, the stylesheet swapped in
			// the theme editor) fires this instead, and never animationend.
			exitNode.addEventListener('animationcancel', onExitEnd);
			// Safety net, not the mechanism: a backgrounded tab, a paused animation
			// or a dropped event must not strand the page lock. Deliberately longer
			// than the animation so it never pre-empts the real signal.
			closeTimer = window.setTimeout(finishClose, ms + 120);
		}

		function sync() {
			var isOpen = modal.hasAttribute('open');

			if (isOpen) {
				// Re-opened during its own exit: drop the exit, keep the lock — the
				// page never became scrollable, so there is nothing to re-take.
				if (closing) endExitPhase();
				if (rendered) return;
				rendered = true;
				lockPage();
				return;
			}

			// Already gone, or already going.
			if (!rendered || closing) return;
			beginClose();
		}

		if (typeof window.MutationObserver === 'function') {
			new window.MutationObserver(sync).observe(modal, {
				attributes: true,
				attributeFilter: ['open']
			});
		}

		// A section deleted or re-rendered in the theme editor takes its modal out
		// of the DOM without ever clearing [open], and the observer dies with it —
		// which would strand the lock on <html> and leave the editor unscrollable.
		// The node is about to be discarded, so there is no exit to play: cancel it
		// and let the page go in the same breath.
		modal.addEventListener('haki-sg:unlock', function () {
			if (closing) endExitPhase();
			rendered = false;
			releasePage();
		});
	}

	// Fired before a modal node is discarded, so watchOpenState can release the page.
	function releaseLock(node) {
		try {
			node.dispatchEvent(new CustomEvent('haki-sg:unlock'));
		} catch (e) {
			/* ancient browser without the CustomEvent constructor — nothing to do */
		}
	}

	function parsePayload(scriptNode) {
		if (!scriptNode) return null;
		var text = scriptNode.textContent;
		if (!text || !text.trim()) return null;
		var data;
		try {
			data = JSON.parse(text);
		} catch (e) {
			return null; // malformed -> zero state, render nothing at all
		}
		if (!data || typeof data !== 'object') return null;
		if (!Array.isArray(data.sections)) return null;
		return data;
	}

	function teardown(trigger, modal) {
		if (trigger && trigger.parentNode) trigger.parentNode.removeChild(trigger);
		if (modal) {
			releaseLock(modal);
			if (modal.parentNode) modal.parentNode.removeChild(modal);
		}
	}

	function init(trigger) {
		if (trigger.hasAttribute('data-haki-sg-ready')) return;
		trigger.setAttribute('data-haki-sg-ready', '');

		var modalId = trigger.getAttribute('data-modal-id');
		// <modal-dialog> relocates itself to <body> in connectedCallback, so never
		// look it up relative to the section. In the theme editor a re-rendered
		// section leaves its previous modal behind in <body> (it is no longer a
		// child of the section, so it does not get swapped out with it) — that
		// duplicate id would shadow the live one for both getElementById and
		// <modal-opener>'s data-modal lookup. The newest copy is always the last
		// one appended to <body>, so keep that and bin the rest.
		if (modalId) {
			var dupes = document.querySelectorAll('.haki-sg-modal[id="' + modalId + '"]');
			for (var d = 0; d < dupes.length - 1; d++) {
				releaseLock(dupes[d]);
				if (dupes[d].parentNode) dupes[d].parentNode.removeChild(dupes[d]);
			}
		}
		var modal = modalId ? document.getElementById(modalId) : null;
		if (!modal) {
			teardown(trigger, null);
			return;
		}

		var data = parsePayload(modal.querySelector('[data-haki-sg-data]'));
		if (!data) {
			teardown(trigger, modal);
			return;
		}

		var sections = data.sections.filter(isRenderable);

		var cfg = data.unitConfig && typeof data.unitConfig === 'object' ? data.unitConfig : {};
		var maxDecimals = Number(cfg.maxDecimals);
		if (!isFinite(maxDecimals) || maxDecimals < 0 || maxDecimals > 6) maxDecimals = 1;

		var state = {unit: systemToUnit(cfg.defaultSystem), maxDecimals: maxDecimals};

		var body = modal.querySelector('[data-haki-sg-body]');
		var toggle = modal.querySelector('[data-haki-sg-toggle]');
		var tabsHost = modal.querySelector('[data-haki-sg-tabs]');
		if (!body) {
			teardown(trigger, modal);
			return;
		}

		/* recommender ------------------------------------------------------- */
		// The engine's own sanitiser is the gate: it returns null for a config
		// that is off, points at a table that no longer exists, or names columns
		// that were deleted. Rather than re-checking any of that here — a second
		// gate written from memory is how the two copies start disagreeing.
		var uid = modal.getAttribute('data-section-id') || modalId || 'sg';
		var HS = engine();
		var recConfig = null;
		if (HS && data.recommender) {
			try {
				recConfig = HS.sanitizeRecommender(data.recommender, data.sections);
			} catch (e) {
				recConfig = null;
			}
		}
		var rec = recConfig ? buildRecommender(data, recConfig, uid, state) : null;

		// A chart can legitimately be a recommender with no visible grid at all
		// (PLM's "hide table" keeps a table as the scoring source only), so an
		// empty render list is only a zero state when there is no recommender
		// either.
		if (!sections.length && !rec) {
			teardown(trigger, modal);
			return;
		}

		var liveCells = [];
		// The accessible name for each table's scroll region. Read back off the
		// heading the Liquid snippet already composed ("Tee Sizing Chart – Romance
		// Dawn Tee") rather than rebuilt from the payload: the product half of that
		// string only exists in Liquid, and a second copy of the composition rule
		// here is how the visible title and the announced one start disagreeing.
		// Never data.name — see the payload contract at the top of this file.
		var titleNode = modal.querySelector('.haki-sg__title');
		var label = (titleNode && titleNode.textContent.trim()) || 'Size guide';

		// With no second tab the sections go straight into the body, exactly as
		// before — no wrapper, no roles, no visual change to the eleven charts
		// live today.
		var useTabs = !!(rec && tabsHost && sections.length);
		var chartPanel = body;
		if (useTabs) {
			chartPanel = el('div', 'haki-sg__panel');
			chartPanel.id = 'HakiSgPanelChart-' + uid;
			body.appendChild(chartPanel);
		}

		// Two columns on desktop — see the "desktop two columns" block in
		// haki-size-guide.css for why (a square diagram in a single column pushed
		// the modal off the bottom of a 1080p screen). The grouping is by section
		// KIND rather than position, and each column keeps payload order, so a
		// chart with two tables, two diagrams or an image-first order all still
		// lay out sensibly — nothing here assumes the table/image/text order Haki
		// happens to author today. With no image, or with nothing BUT images,
		// there is no wrapper at all and the panel is byte-for-byte what it was.
		var imageCount = 0;
		sections.forEach(function (s) {
			if (s.type === 'image') imageCount++;
		});
		var split = imageCount > 0 && imageCount < sections.length;

		var cols = null;
		var mainHost = chartPanel;
		var asideHost = chartPanel;
		if (split) {
			cols = el('div', 'haki-sg__cols');
			mainHost = el('div', 'haki-sg__col haki-sg__col--main');
			asideHost = el('div', 'haki-sg__col haki-sg__col--aside');
			cols.appendChild(mainHost);
			cols.appendChild(asideHost);
			chartPanel.appendChild(cols);
		}

		// Collected while building, for fitInstructionBoxes(): each framed
		// instruction box and the diagram it is paired with. `groupLabel` is the
		// heading of the divider most recently passed — the composer splices one in
		// above each garment's sections, so on a bundle it is "Hoodie", then
		// "Pants", and on a single-garment chart it stays empty.
		var howtoBoxes = [];
		var firstImage = null;
		var lastImage = null;
		var groupLabel = '';

		sections.forEach(function (section) {
			var built = null;
			if (section.type === 'table') built = buildTable(section, label, state);
			else if (section.type === 'image') built = buildImage(section);
			else if (section.type === 'text') built = buildText(section, split, groupLabel);
			else if (section.type === 'divider') {
				built = buildDivider(section);
				groupLabel = typeof section.label === 'string' ? section.label.trim() : '';
			}
			if (!built) return;
			(section.type === 'image' ? asideHost : mainHost).appendChild(built.node);
			if (built.image) {
				lastImage = built.image;
				if (!firstImage) firstImage = built.image;
			}
			if (built.scroller) {
				howtoBoxes.push({node: built.node, scroller: built.scroller, image: lastImage});
			}
			if (built.cells.length) liveCells = liveCells.concat(built.cells);
		});

		// Prose authored ahead of every diagram: pair it with the first one rather
		// than leaving it uncapped. On the only shape where that differs from the
		// rule above — a single-garment chart written text-first — the first diagram
		// IS this box's diagram.
		howtoBoxes.forEach(function (box) {
			if (!box.image) box.image = firstImage;
		});
		var howtoFit = fitInstructionBoxes(modal, howtoBoxes);

		// Stacked on a phone, the diagram comes FIRST. See orderColumns().
		if (cols) orderColumns(cols, mainHost, asideHost);
		warmDiagrams(trigger, chartPanel.querySelectorAll('.haki-sg__image'));

		// Widen the shell for the layouts that actually have a second column to
		// fill. A plain one-column chart keeps the 720px measure it reads best at.
		if (split || rec) modal.setAttribute('data-haki-sg-wide', '');

		if (rec) {
			rec.node.id = 'HakiSgPanelRec-' + uid;
			if (useTabs) rec.node.classList.add('haki-sg__panel');
			body.appendChild(rec.node);
		}

		if (useTabs) {
			var tabChart = makeTabButton('HakiSgTabChart-' + uid, chartPanel.id, 'Size chart');
			var tabRec = makeTabButton('HakiSgTabRec-' + uid, rec.node.id, 'What’s my size?');
			tabsHost.appendChild(tabChart);
			tabsHost.appendChild(tabRec);
			tabsHost.hidden = false;
			wireTabs(tabsHost, [tabChart, tabRec], [chartPanel, rec.node], null);
		} else if (tabsHost && tabsHost.parentNode) {
			tabsHost.parentNode.removeChild(tabsHost);
		}

		/* unit toggle ------------------------------------------------------- */
		// The recommender's fit readout is rendered in the same unit system, so the
		// toggle earns its place even on a chart whose only table is hidden.
		var showToggle = cfg.showToggle !== false && (liveCells.length > 0 || !!rec);
		if (toggle && showToggle) {
			toggle.hidden = false;
			var buttons = toggle.querySelectorAll('[data-haki-sg-unit]');
			var paint = function () {
				Array.prototype.forEach.call(buttons, function (btn) {
					var on = btn.getAttribute('data-haki-sg-unit') === state.unit;
					btn.setAttribute('aria-pressed', on ? 'true' : 'false');
					btn.classList.toggle('is-active', on);
				});
			};
			var rerender = function () {
				liveCells.forEach(function (entry) {
					entry.node.textContent = cellText(
						entry.cell,
						entry.columnUnit,
						state.unit,
						state.maxDecimals
					);
				});
				// Same toggle, same numbers: the fit readout would otherwise sit in
				// inches next to a table the shopper just switched to cm.
				if (rec) rec.refresh();
				// Cells are nowrap, so in -> cm does not usually re-wrap a row — but a
				// chart with a wrapping header cell can change the table's height, and
				// every box below it is measured from where that table ends.
				howtoFit.schedule();
			};
			Array.prototype.forEach.call(buttons, function (btn) {
				btn.addEventListener('click', function () {
					var next = btn.getAttribute('data-haki-sg-unit');
					if (next === state.unit) return;
					state.unit = next;
					paint();
					rerender();
				});
			});
			paint();
		} else if (toggle && toggle.parentNode) {
			toggle.parentNode.removeChild(toggle);
		}

		// Everything rendered: reveal the trigger. It ships `hidden` so a product
		// with a missing/empty/malformed chart never flashes a dead link.
		trigger.hidden = false;

		watchOpenState(trigger, modal);
	}

	function initAll(scope) {
		var nodes = (scope || document).querySelectorAll('[data-haki-size-guide]');
		Array.prototype.forEach.call(nodes, init);
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', function () {
			initAll(document);
		});
	} else {
		initAll(document);
	}

	// Theme editor: the section re-renders on every setting change. Duplicate
	// modals left in <body> are pruned inside init() (see the comment there).
	document.addEventListener('shopify:section:load', function (event) {
		initAll(event.target);
	});

	// Section deleted outright: its modal lives in <body> and would otherwise stay.
	document.addEventListener('shopify:section:unload', function (event) {
		var sectionId = event.detail && event.detail.sectionId;
		if (!sectionId) return;
		var stale = document.querySelectorAll(
			'body > .haki-sg-modal[data-section-id="' + sectionId + '"]'
		);
		Array.prototype.forEach.call(stale, function (node) {
			releaseLock(node);
			node.parentNode.removeChild(node);
		});
	});

	// Exposed for manual QA in the console — not used by the theme.
	window.HakiSizeGuide = {cellText: cellText, formatNumber: formatNumber};
})();
